import {
  DataType,
  type EntityType,
  getAllowedChartTypesForMetric,
  getMetricDataTypeCapability,
} from '@revelio/core';

import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import type {
  ChartType,
  SelectableChartType,
} from '../../PageConfigProvider/types/chart';
import {
  groupedMetricOptions,
  type MetricValues,
} from '../../PageConfigProvider/types/metric';
import { isSelectableChartType } from './is-selectable-chart-type';

export const PRIMARY_FILTER_TO_ENTITY_TYPE: Record<
  PrimaryFilterId,
  EntityType
> = {
  [FilterId.COMPANY]: 'company',
  [FilterId.GEOGRAPHY]: 'geography',
  [FilterId.ROLE]: 'role',
};

const DIVERGING_BAR_METRICS: MetricValues[] = [
  'headcount',
  'overall',
  'sentiment_top_bottom',
];

const GROUPED_BAR_METRICS_BY_ENTITY: Record<EntityType, MetricValues[]> = {
  company: [
    'base_salary',
    'contractor_pay_rate',
    'headcount',
    'posting_salary',
    'postings_active',
    'postings_new',
    'postings_removed',
    'time_to_fill',
    'total_compensation',
    'total_spend_compensation',
    'total_spend_salary',
    'transitions_inflow',
    'transitions_outflow',
  ],
  geography: [
    'base_salary',
    'contractor_pay_rate',
    'headcount',
    'postings_active',
    'total_compensation',
    'total_spend_compensation',
    'total_spend_salary',
    'transitions_inflow',
    'transitions_outflow',
  ],
  role: [
    'headcount',
    'postings_active',
    'total_spend_compensation',
    'total_spend_salary',
    'transitions_inflow',
    'transitions_outflow',
  ],
};

function isMetricAllowedForChartBuilder(
  chartType: SelectableChartType,
  metric: MetricValues,
  entityType: EntityType
): boolean {
  const allowedCharts = getAllowedChartTypesForMetric(metric, entityType);
  if (!allowedCharts.includes(chartType)) return false;

  if (chartType === 'diverging-bar' || chartType === 'diverging-bar-combo') {
    return DIVERGING_BAR_METRICS.includes(metric);
  }

  if (chartType === 'grouped-bar') {
    return GROUPED_BAR_METRICS_BY_ENTITY[entityType].includes(metric);
  }

  if (chartType === 'volume') {
    return metric === 'postings_volume';
  }

  // Bar charts use a single-entity snapshot; exclude metrics whose default shape
  // is breakdown, timeseries, or table rows (line/stacked-bar/grouped-bar cover those).
  if (chartType === 'bar') {
    return (
      getMetricDataTypeCapability(metric).defaultDataType === DataType.SINGLE
    );
  }

  return true;
}

// Invert the matrix: for a given chartType and entityType, find all metrics that support it
function getMetricsForChartType(
  chartType: SelectableChartType,
  entityType: EntityType
): MetricValues[] {
  if (chartType === 'ai-summary' || chartType === 'gauge') return [];

  const allMetrics = groupedMetricOptions.flatMap((group) =>
    group.options.map((option) => option.value)
  );

  return allMetrics.filter((metric) =>
    isMetricAllowedForChartBuilder(chartType, metric, entityType)
  );
}

export const getAvailableMetrics = ({
  primaryFilterId,
  chartType,
}: {
  primaryFilterId?: PrimaryFilterId;
  chartType?: ChartType;
}) => {
  if (!primaryFilterId || !isSelectableChartType(chartType)) return [];

  const entityType = PRIMARY_FILTER_TO_ENTITY_TYPE[primaryFilterId];
  if (!entityType) return [];

  const metrics = getMetricsForChartType(chartType, entityType);
  if (metrics.length === 0) return [];

  return groupedMetricOptions
    .map((group) => {
      const matchedOptions = group.options.filter((option) =>
        metrics.includes(option.value)
      );

      if (matchedOptions.length === 0) return null;

      return { ...group, options: matchedOptions };
    })
    .filter((group) => group !== null);
};
